import { createContext, useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { supabase } from '../services/supabase'
import { useProfile } from '../hooks/useProfile'
import type { User, UserRole } from '../types'
import type { Session } from '@supabase/supabase-js'
import posthog from 'posthog-js'
import { isInternalTraffic } from '../utils/posthogFilter'

interface AuthContextType {
  user: User | null
  loading: boolean
  hydrated: boolean // Indicates if the user state is fully initialized
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  signUp: (email: string, password: string) => Promise<void>
  isRole: (roles: UserRole[]) => boolean
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [sessionLoading, setSessionLoading] = useState(true) // Tracks session initialization
  const [hydrated, setHydrated] = useState(false) // Tracks whether user state is fully initialized
  const queryClient = useQueryClient()

  // Fetch the profile using the useProfile hook
  const { data: profile, isLoading: profileLoading } = useProfile(
    session?.user?.id || null,
    session?.user?.email || null
  )

  // Update the user state when the profile changes
  useEffect(() => {
    if (!session?.user) {
      setUser(null)
      setHydrated(true) // Mark as hydrated immediately if no session
      return
    }

    // Only update user and hydration state when profile loading is complete
    if (!profileLoading) {
      if (profile) {
        setUser(profile)
        setHydrated(true)
      } else {
        setUser(null)
        setHydrated(true)
      }
    }
  }, [profile, profileLoading, session])

  // Handle session changes
  const updateUserState = async (newSession: Session | null) => {
    setSession(newSession)
    if (!newSession?.user) {
      setUser(null)
      setHydrated(true)
    } else {
      // Only reset hydrated state if the user ID actually changed
      // Don't reset if user is temporarily undefined during profile loading
      if (user && user.id !== newSession.user.id) {
        setHydrated(false)
      }
    }
  }

  useEffect(() => {
    // Fetch the initial session
    setSessionLoading(true)
    supabase.auth.getSession().then(({ data: { session } }) => {
      updateUserState(session)
      setSessionLoading(false) // Session is now loaded
    })

    // Subscribe to auth state changes
    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((_event, session) => {
      updateUserState(session)

      // Invalidate tours query when auth state changes
      queryClient.invalidateQueries({ queryKey: ['tours'] })
      queryClient.invalidateQueries({ queryKey: ['cityCounts'] })
      queryClient.invalidateQueries({ queryKey: ['categoryCounts'] })
    })

    return () => subscription.unsubscribe()
  }, [queryClient])

  // Combine loading states
  const loading = sessionLoading || profileLoading || Boolean(session?.user && !user)

  // PostHog user identification and property setting
  //
  // This effect sets super properties on all PostHog events:
  // - is_internal_user: boolean (true if user has localStorage flag set)
  // - user_role: string | null (user's role: 'admin', 'creator', 'traveler', or null)
  //
  // These properties are automatically attached to every event, so you can filter them
  // in PostHog insights and dashboards:
  // 1. In any insight/dashboard, add a filter: "is_internal_user" = false
  // 2. Optionally filter by "user_role" to exclude specific roles (e.g., exclude 'admin')
  //
  // Note: You don't need to configure anything in PostHog settings. The properties
  // are set automatically, and you simply filter them in your insights/dashboards.
  useEffect(() => {
    // Wait for auth state to be fully determined
    if (!hydrated) {
      return
    }

    try {
      // Comprehensive check for internal traffic (localStorage flag)
      const { isInternal } = isInternalTraffic()

      if (!user) {
        // User is not logged in - check for internal traffic via localStorage
        posthog.reset()

        // Set internal flag if detected (localStorage)
        posthog.register({
          is_internal_user: isInternal,
          user_role: null
        })

        // Capture pageview after properties are set
        posthog.capture('$pageview')
      } else {
        // User is logged in - identify and set properties
        posthog.identify(user.id, {
          email: user.email
        })

        // Set properties for filtering
        posthog.register({
          is_internal_user: isInternal, // Catches all internal traffic (localStorage)
          user_role: user.role
        })

        // Capture pageview after properties are set
        posthog.capture('$pageview')
      }
    } catch (error) {
      // PostHog may not be initialized, ignore errors
      if (error instanceof Error) {
        console.warn('PostHog operations failed:', error.message)
      }
    }
  }, [user, hydrated])

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password })
      if (error) throw error
    } catch (err) {
      throw err
    }
  }

  const signUp = async (email: string, password: string) => {
    try {
      // 1. Check if email exists in profiles
      const { data: existing, error: checkError } = await supabase
        .from('profiles')
        .select('id')
        .eq('email', email)
        .maybeSingle()

      if (checkError) {
        throw checkError
      }
      if (existing) {
        throw new Error('User with this email already exists')
      }

      // 2. Proceed with signup
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { role: 'traveler' },
          emailRedirectTo: `${window.location.origin}/email-confirmation`
        }
      })
      if (error) throw error
      if (!data.user) throw new Error('Failed to create user account')
    } catch (err) {
      throw err
    }
  }

  const signOut = async () => {
    setSessionLoading(true) // Set session loading during sign-out
    try {
      const { error } = await supabase.auth.signOut()
      if (error) throw error
      setUser(null)
      setSession(null)
      setHydrated(true) // Ensure hydrated remains true after logout
    } finally {
      setSessionLoading(false) // Reset session loading after sign-out
    }
  }

  const isRole = (roles: UserRole[]) => {
    return user ? roles.includes(user.role) : false
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        hydrated,
        signIn,
        signOut,
        signUp,
        isRole
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
